import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Upload, X, FolderOpen, Image, Layers, Check, 
  AlertCircle, ChevronRight, Edit2 
} from 'lucide-react';
import {
  UploadGroup,
  UploadState,
} from '../types';
import { getHdrMergePipeline, uploadListingWithMedia } from '../services/supabase';
import SmartUploader from './SmartUploader';
import { HDR_CREDIT_COPY } from '@rehub/pricing';
import type { HdrEntitlement } from '../services/hdr';
import { trackHdrEvent } from '../services/analytics';

interface UploadPageProps {
  userId: string;
  credits?: number;
  entitlement?: HdrEntitlement | null;
  onBuyCredits?: () => void;
}

const UploadPage: React.FC<UploadPageProps> = ({ userId, credits = 0, entitlement, onBuyCredits }) => {
  const navigate = useNavigate();
  
  const [uploadState, setUploadState] = useState<UploadState>({
    status: 'idle',
    progress: 0,
    totalFiles: 0,
    uploadedFiles: 0,
    groups: [],
  });

  const [selectedGroupIndex, setSelectedGroupIndex] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string | null>(null);
  const handleFilesAnalyzed = (groups: UploadGroup[]) => {
    const totalFiles = groups.reduce((acc, g) => 
      acc + g.stacks.reduce((a, s) => a + s.files.length, 0) + g.singleImages.length, 0
    );
    
    setUploadState({
      status: 'idle',
      progress: 0,
      totalFiles,
      uploadedFiles: 0,
      groups,
    });
    
    if (groups.length > 0) {
      setSelectedGroupIndex(0);
    }
  };

  const updateGroupName = (index: number, newName: string) => {
    setUploadState(prev => ({
      ...prev,
      groups: prev.groups.map((g, i) => 
        i === index ? { ...g, name: newName } : g
      ),
    }));
    setEditingName(null);
  };

  const removeStack = (groupIndex: number, stackIndex: number) => {
    setUploadState(prev => ({
      ...prev,
      groups: prev.groups.map((g, gi) => {
        if (gi !== groupIndex) return g;
        return {
          ...g,
          stacks: g.stacks.filter((_, si) => si !== stackIndex),
        };
      }).filter(g => g.stacks.length > 0 || g.singleImages.length > 0),
    }));
  };

  const removeSingleImage = (groupIndex: number, imageIndex: number) => {
    setUploadState(prev => ({
      ...prev,
      groups: prev.groups.map((g, gi) => {
        if (gi !== groupIndex) return g;
        return {
          ...g,
          singleImages: g.singleImages.filter((_, ii) => ii !== imageIndex),
        };
      }).filter(g => g.stacks.length > 0 || g.singleImages.length > 0),
    }));
  };

  const handleUpload = async () => {
    if (uploadState.groups.length === 0) return;
    if (insufficientAllowance) return;

    setUploadState(prev => ({ ...prev, status: 'uploading', progress: 0 }));

    let successCount = 0;
    const totalGroups = uploadState.groups.length;

    for (const group of uploadState.groups) {
      try {
        const listing = await uploadListingWithMedia(
          userId,
          group,
          (progress) => {
            setUploadState(prev => ({
              ...prev,
              progress: Math.round((successCount / totalGroups) * 100 + (progress / totalGroups)),
            }));
          },
          { hdrMergePipeline: await getHdrMergePipeline() }
        );
        
        if (listing) {
          successCount++;
          trackHdrEvent('hdr_image_uploaded', {
            allowance: entitlement?.allowance,
            remaining: entitlement?.remaining,
            plan: entitlement?.plan_id,
          });
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
    }

    setUploadState(prev => ({ 
      ...prev, 
      status: 'complete',
      progress: 100,
    }));

    // Navigate to listings after short delay
    setTimeout(() => {
      navigate('/listings');
    }, 1500);
  };

  const selectedGroup = selectedGroupIndex !== null ? uploadState.groups[selectedGroupIndex] : null;

  // Output images = one per HDR stack + one per single image. Each costs
  // HDR_CREDIT_COST. The database trigger is authoritative for the real charge;
  // this estimate just keeps the UI honest.
  const totalOutputImages = uploadState.groups.reduce(
    (acc, g) => acc + g.stacks.length + g.singleImages.length,
    0,
  );
  const remaining = entitlement?.remaining ?? 0;
  const hasEstimate = totalOutputImages > 0;
  const insufficientAllowance = hasEstimate && remaining < totalOutputImages;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Upload Photos</h1>
        <p className="text-slate-400 mt-1">
          Drop your bracketed photos and we'll automatically detect HDR stacks.
        </p>
        <p className="text-sm text-emerald-400 mt-2">{HDR_CREDIT_COPY.optimisedHelper}</p>
      </div>

      {/* HDR Credits estimate */}
      {hasEstimate && (
        <div
          className={`mb-6 rounded-xl border p-4 flex flex-wrap items-center justify-between gap-3 ${
            insufficientAllowance
              ? 'border-amber-500/40 bg-amber-500/10'
              : 'border-slate-700 bg-slate-800/50'
          }`}
        >
          <div className="text-sm">
            <span className="text-slate-300">
              {totalOutputImages} HDR Credit{totalOutputImages === 1 ? '' : 's'} this upload
            </span>
            <span className="text-slate-500">
              {' '}· {remaining} remaining this month
            </span>
          </div>
          {insufficientAllowance && (
            <Link
              to="/pricing"
              className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-500"
            >
              Upgrade plan
            </Link>
          )}
        </div>
      )}

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Upload zone or group list */}
        <div className="lg:col-span-2">
          {uploadState.groups.length === 0 ? (
            <SmartUploader onFilesAnalyzed={handleFilesAnalyzed} />
          ) : (
            <div className="space-y-4">
              {/* Upload status */}
              {uploadState.status === 'uploading' && (
                <div className="bg-brand-500/10 border border-brand-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-brand-400 font-medium">Uploading...</span>
                  </div>
                  <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-brand-500 transition-all duration-300"
                      style={{ width: `${uploadState.progress}%` }}
                    />
                  </div>
                  <p className="text-sm text-slate-400 mt-2">
                    {uploadState.progress}% complete
                  </p>
                </div>
              )}

              {uploadState.status === 'complete' && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 flex items-center gap-3">
                  <Check className="w-6 h-6 text-emerald-400" />
                  <span className="text-emerald-400 font-medium">
                    Upload complete! Redirecting...
                  </span>
                </div>
              )}

              {/* Group cards */}
              {uploadState.groups.map((group, index) => (
                <div
                  key={group.id}
                  onClick={() => setSelectedGroupIndex(index)}
                  className={`
                    bg-slate-800/50 border rounded-xl p-4 cursor-pointer transition-all
                    ${selectedGroupIndex === index 
                      ? 'border-brand-500 ring-2 ring-brand-500/20' 
                      : 'border-slate-700 hover:border-slate-600'
                    }
                  `}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FolderOpen className="w-5 h-5 text-brand-400" />
                      </div>
                      <div className="min-w-0">
                        {editingName === group.id ? (
                          <input
                            type="text"
                            defaultValue={group.name}
                            autoFocus
                            onBlur={(e) => updateGroupName(index, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updateGroupName(index, e.currentTarget.value);
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-brand-500"
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-white truncate">
                              {group.name}
                            </h3>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingName(group.id);
                              }}
                              className="p-1 text-slate-400 hover:text-white"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                        <p className="text-sm text-slate-400">
                          {group.stacks.length} stacks, {group.singleImages.length} single
                        </p>
                      </div>
                    </div>
                    <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${
                      selectedGroupIndex === index ? 'rotate-90' : ''
                    }`} />
                  </div>
                </div>
              ))}

              {/* Add more button */}
              <button
                onClick={() => setUploadState(prev => ({ ...prev, groups: [] }))}
                className="w-full py-3 border-2 border-dashed border-slate-700 rounded-xl text-slate-400 hover:text-white hover:border-slate-600 transition-colors"
              >
                + Add more photos
              </button>
            </div>
          )}
        </div>

        {/* Right: Selected group details */}
        <div className="lg:col-span-1">
          {selectedGroup ? (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 sticky top-6">
              <h3 className="font-semibold text-white mb-4">
                {selectedGroup.name}
              </h3>

              {/* Stacks */}
              {selectedGroup.stacks.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                    HDR Stacks ({selectedGroup.stacks.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedGroup.stacks.map((stack, stackIndex) => (
                      <div 
                        key={stack.id}
                        className="flex items-center justify-between p-2 bg-slate-700/50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <Layers className="w-4 h-4 text-brand-400" />
                          <span className="text-sm text-white">
                            {stack.files.length} exposures
                          </span>
                        </div>
                        <button
                          onClick={() => removeStack(selectedGroupIndex!, stackIndex)}
                          className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Single images */}
              {selectedGroup.singleImages.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                    Single Images ({selectedGroup.singleImages.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedGroup.singleImages.map((img, imgIndex) => (
                      <div 
                        key={imgIndex}
                        className="flex items-center justify-between p-2 bg-slate-700/50 rounded-lg"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Image className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="text-sm text-white truncate">
                            {img.file.name}
                          </span>
                        </div>
                        <button
                          onClick={() => removeSingleImage(selectedGroupIndex!, imgIndex)}
                          className="p-1 text-slate-400 hover:text-red-400 transition-colors flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload button */}
              <button
                onClick={handleUpload}
                disabled={uploadState.status === 'uploading' || uploadState.groups.length === 0 || insufficientAllowance}
                className="w-full py-3 bg-brand-500 hover:bg-brand-600 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Upload className="w-5 h-5" />
                Upload All ({uploadState.groups.length} listings)
              </button>
            </div>
          ) : uploadState.groups.length > 0 ? (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 sticky top-6 space-y-4">
              <p className="text-sm text-slate-400">
                Select a listing on the left for stack details, or upload all.
              </p>
              <button
                onClick={handleUpload}
                disabled={uploadState.status === 'uploading'}
                className="w-full py-3 bg-brand-500 hover:bg-brand-600 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Upload className="w-5 h-5" />
                Upload All ({uploadState.groups.length} listings)
              </button>
            </div>
          ) : (
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-8 text-center">
              <Image className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">
                Select a listing to view details
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadPage;
